"use client";

import { useState } from "react";

type CapabilityKey =
  | "governance"
  | "spatial"
  | "edge"
  | "devices"
  | "automation"
  | "intelligence"
  | "wall";

const capabilities: Record<
  CapabilityKey,
  {
    index: string;
    label: string;
    eyebrow: string;
    title: string;
    copy: string;
    bullets: string[];
    stats: { value: string; label: string }[];
  }
> = {
  governance: {
    index: "01",
    label: "Portfolio",
    eyebrow: "Multi-tenant control plane",
    title: "Model the real property, then govern it clearly.",
    copy:
      "HUD organizes operations from portfolio to room: organizations, buildings, floors, spaces, memberships and tenant assignments live in one structured operating model.",
    bullets: [
      "Owner, Administrator, Manager and Viewer membership roles",
      "Building, floor, space and tenant-controlled-space hierarchy",
      "Common-space, occupancy, parent-space and status modeling",
      "Manager and portfolio-level operating surfaces",
    ],
    stats: [
      { value: "4", label: "defined role types" },
      { value: "5", label: "property levels" },
    ],
  },
  spatial: {
    index: "02",
    label: "Spatial",
    eyebrow: "Operational floor plans",
    title: "Turn a diagram into a live operating surface.",
    copy:
      "Upload a building diagram, trace rooms, bind them to operational spaces, place devices, reconcile the model and publish a controlled layout version for the HUD and wall panel.",
    bullets: [
      "Versioned floor-layout drafts and controlled publication",
      "Room polygon tracing with grid, angle and wall assistance",
      "Drag-and-keyboard device placement with visual sizing controls",
      "Binding, duplicate-assignment and readiness diagnostics",
    ],
    stats: [
      { value: "6", label: "accepted asset formats" },
      { value: "20 MB", label: "diagram limit" },
    ],
  },
  edge: {
    index: "03",
    label: "Edge runtime",
    eyebrow: "Local execution and continuity",
    title: "Keep essential building control close to the devices.",
    copy:
      "A locally deployed HUD Edge Agent enrolls into the SaaS authority, reports heartbeat and health, discovers supported devices, executes authorized commands and preserves bounded operational continuity.",
    bullets: [
      "One-time gateway enrollment identity and secret issuance",
      "Building-wide or scoped floor, room and layout-device ownership",
      "Local registry, command polling, persistence, recovery and state sync",
      "Local runtime bridge and static wall-panel serving path",
    ],
    stats: [
      { value: "30 s", label: "default heartbeat" },
      { value: "2 s", label: "default command poll" },
    ],
  },
  devices: {
    index: "04",
    label: "Devices",
    eyebrow: "Discovery, registry and command evidence",
    title: "A canonical device record from discovery to reconciliation.",
    copy:
      "HUD normalizes discovered identities into governed devices, binds them to spaces and gateways, validates runtime readiness, records every command stage and reconciles the latest reported state.",
    bullets: [
      "Home Assistant discovery and import workflows",
      "HomeKit pairing path and tested Leviton dimmer adapter",
      "On/off, brightness, scene and supported runtime commands",
      "Queued, accepted, dispatched, acknowledged, completed or failed trace",
    ],
    stats: [
      { value: "7", label: "command evidence stages" },
      { value: "1", label: "canonical device identity" },
    ],
  },
  automation: {
    index: "05",
    label: "Automation",
    eyebrow: "Rules on the standard runtime path",
    title: "Define, qualify and observe automation without a shadow runtime.",
    copy:
      "Rules combine schedules or events, evaluated conditions and ordered actions. Readiness checks validate scope, target capability and gateway state before actions enter the normal DeviceCommand lifecycle.",
    bullets: [
      "Daily, weekly and one-time schedules with timezone support",
      "Device-state, occupancy and command-result trigger definitions",
      "Any/all condition modes, cooldowns, priorities and delays",
      "Execution history with generated command evidence",
    ],
    stats: [
      { value: "5", label: "rule classifications" },
      { value: "11", label: "action commands modeled" },
    ],
  },
  intelligence: {
    index: "06",
    label: "Intelligence",
    eyebrow: "Fleet observability and guarded AI",
    title: "Move from scattered device data to operating intelligence.",
    copy:
      "Portfolio dashboards compare buildings, gateways, devices, commands and automation reliability. AI Operations and AI Chat provide deterministic, read-only observations over that enterprise context.",
    bullets: [
      "Organization, building, floor, space, device and gateway dashboards",
      "Gateway, device and automation fleet-health rankings",
      "Runtime readiness, integrity, usage and diagnostics surfaces",
      "Read-only AI summaries with explicit no-action guardrails",
    ],
    stats: [
      { value: "0", label: "AI-created commands" },
      { value: "6+", label: "runtime scopes" },
    ],
  },
  wall: {
    index: "07",
    label: "Wall panel",
    eyebrow: "Room-level resident experience",
    title: "A graphical HUD for touch, tablet, phone and kiosk use.",
    copy:
      "The responsive wall-panel surface projects published rooms and bound devices into an accessible control experience, with runtime binding, readiness, queue, audit and operator diagnostics close at hand.",
    bullets: [
      "Desktop, tablet and phone portrait/landscape layouts in source",
      "Lighting, dimmer, scene, climate, media and sprinkler surfaces",
      "Mount profiles, session lock, maintenance and wall-policy controls",
      "Local audit export, readiness evidence and reconciliation views",
    ],
    stats: [
      { value: "60", label: "local audit records retained" },
      { value: "3", label: "mount profiles" },
    ],
  },
};

const commandSteps = [
  {
    title: "Intent",
    body: "An operator, wall panel or automation creates a bounded command request.",
  },
  {
    title: "Authority",
    body: "SaaS validates tenant context, ownership, target capability and policy.",
  },
  {
    title: "Queue",
    body: "A persistent DeviceCommand becomes the shared execution record.",
  },
  {
    title: "Edge",
    body: "The owning gateway claims and routes the command through its local registry.",
  },
  {
    title: "Device",
    body: "The protocol adapter executes against the verified physical target.",
  },
  {
    title: "Reconcile",
    body: "Result and refreshed state return to HUD for evidence and operator review.",
  },
];

const roleCards = [
  {
    role: "Owner",
    tag: "Accountable authority",
    copy: "Creates the portfolio model, governs membership, enrollment, integrations and operating policy.",
  },
  {
    role: "Administrator",
    tag: "Configuration lead",
    copy: "Builds floors, spaces, layouts, gateways, devices and automations under owner policy.",
  },
  {
    role: "Manager",
    tag: "Operations surface",
    copy: "Reviews fleet health, runtime readiness, commands, automation reliability and escalations.",
  },
  {
    role: "Viewer",
    tag: "Read-only posture",
    copy: "Consumes authorized status and evidence without becoming the operational decision-maker.",
  },
];

function Mark({ small = false }: { small?: boolean }) {
  return (
    <span className={`brand-mark${small ? " brand-mark--small" : ""}`} aria-hidden="true">
      <span />
      <span />
      <span />
      <span />
    </span>
  );
}

function Arrow() {
  return <span aria-hidden="true">↗</span>;
}

export default function Home() {
  const [activeCapability, setActiveCapability] =
    useState<CapabilityKey>("governance");
  const [traceStep, setTraceStep] = useState(2);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [runtimeMode, setRuntimeMode] = useState<"online" | "continuity" | "audit">(
    "online",
  );
  const [demoLight, setDemoLight] = useState(true);
  const [brightness, setBrightness] = useState(72);

  const capability = capabilities[activeCapability];

  return (
    <main>
      <header className="site-header">
        <a className="brand" href="#top" aria-label="HUD home">
          <Mark />
          <span className="brand-copy">
            <strong>HUD</strong>
            <small>Smart Building Platform</small>
          </span>
        </a>

        <button
          className="menu-button"
          type="button"
          aria-label="Toggle navigation"
          aria-expanded={mobileMenu}
          onClick={() => setMobileMenu((value) => !value)}
        >
          <span />
          <span />
        </button>

        <nav className={mobileMenu ? "nav nav--open" : "nav"} aria-label="Main navigation">
          {[
            ["Platform", "#platform"],
            ["Architecture", "#architecture"],
            ["Wall Panel", "#wall-panel"],
            ["Operations", "#operations"],
            ["Readiness", "#readiness"],
            ["About", "#about"],
          ].map(([label, href]) => (
            <a key={href} href={href} onClick={() => setMobileMenu(false)}>
              {label}
            </a>
          ))}
        </nav>

        <a className="header-cta" href="#contact">
          Request a briefing <Arrow />
        </a>
      </header>

      <section className="hero section-shell" id="top">
        <div className="hero-copy">
          <div className="eyebrow-pill">
            <span className="pulse-dot" /> Source-verified August 2026 baseline
          </div>
          <h1>
            One operating layer for <em>every building</em> you manage.
          </h1>
          <p className="hero-lead">
            HUD unifies property structure, spatial floor plans, edge gateways, device
            control, automation, fleet intelligence and room-level experiences in one
            evidence-driven smart-building platform.
          </p>
          <div className="hero-actions">
            <a className="button button--primary" href="#platform">
              Explore the platform <Arrow />
            </a>
            <a className="button button--quiet" href="#architecture">
              Follow a command
            </a>
          </div>
          <div className="hero-proof" aria-label="Platform foundations">
            <span><i /> SaaS authority</span>
            <span><i /> Edge continuity</span>
            <span><i /> Spatial control</span>
            <span><i /> Guarded AI</span>
          </div>
        </div>

        <div className="hero-product" aria-label="HUD product model">
          <div className="product-window">
            <div className="window-bar">
              <div className="window-brand"><Mark small /> HUD Runtime</div>
              <div className="window-context">Biscayne Park House <span>⌄</span></div>
              <div className="window-user">JF</div>
            </div>

            <div className="runtime-tabs" role="group" aria-label="Product model mode">
              <button
                className={runtimeMode === "online" ? "is-active" : ""}
                onClick={() => setRuntimeMode("online")}
                type="button"
              >
                Live runtime
              </button>
              <button
                className={runtimeMode === "continuity" ? "is-active" : ""}
                onClick={() => setRuntimeMode("continuity")}
                type="button"
              >
                Continuity
              </button>
              <button
                className={runtimeMode === "audit" ? "is-active" : ""}
                onClick={() => setRuntimeMode("audit")}
                type="button"
              >
                Evidence
              </button>
            </div>

            <div className="runtime-body">
              <div className="runtime-heading">
                <div>
                  <span>Main Floor</span>
                  <strong>
                    {runtimeMode === "online"
                      ? "Live building view"
                      : runtimeMode === "continuity"
                        ? "Local continuity ready"
                        : "Command evidence trail"}
                  </strong>
                </div>
                <span className={`runtime-status runtime-status--${runtimeMode}`}>
                  {runtimeMode === "online"
                    ? "Connected"
                    : runtimeMode === "continuity"
                      ? "Edge local"
                      : "Trace active"}
                </span>
              </div>

              <div className="floor-model">
                <div className="room room--living">
                  <span>Living Room</span><i className="device-dot is-on" />
                </div>
                <div className="room room--kitchen">
                  <span>Kitchen</span><i className="device-dot" />
                </div>
                <div className="room room--office">
                  <span>Office</span><i className="device-dot is-on" />
                </div>
                <div className="room room--suite">
                  <span>Main Suite</span><i className="device-dot is-on" />
                </div>
                <div className="room room--bath">
                  <span>Bath</span><i className="device-dot" />
                </div>
                <div className="room room--patio">
                  <span>Patio</span><i className="device-dot sprinkler" />
                </div>
              </div>

              <div className="runtime-metrics">
                <div><span>Gateway</span><strong>Fresh heartbeat</strong></div>
                <div><span>Readiness</span><strong>Operational</strong></div>
                <div><span>Commands</span><strong>Reconciled</strong></div>
              </div>
            </div>
          </div>
          <div className="product-float product-float--top">
            <span className="float-icon">✓</span>
            <div><small>Runtime check</small><strong>Policy + scope verified</strong></div>
          </div>
          <div className="product-float product-float--bottom">
            <span className="float-icon float-icon--cyan">↻</span>
            <div><small>State reconciliation</small><strong>Physical state refreshed</strong></div>
          </div>
        </div>
      </section>

      <section className="proof-strip" aria-label="HUD platform overview">
        <div><strong>Portfolio → room</strong><span>Structured operating context</span></div>
        <div><strong>SaaS + Edge</strong><span>Central authority, local execution</span></div>
        <div><strong>One command record</strong><span>Intent through reconciliation</span></div>
        <div><strong>Read-only AI</strong><span>Observation without control</span></div>
      </section>

      <section className="platform section-shell section-pad" id="platform">
        <div className="section-intro">
          <div>
            <span className="section-kicker">Complete platform</span>
            <h2>Every operational layer, connected.</h2>
          </div>
          <p>
            The uploaded application is much broader than a home-control dashboard.
            It is an enterprise foundation for modeling, operating, observing and
            governing connected properties.
          </p>
        </div>

        <div className="capability-layout">
          <div className="capability-nav" role="tablist" aria-label="Platform capabilities">
            {(Object.keys(capabilities) as CapabilityKey[]).map((key) => {
              const item = capabilities[key];
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={activeCapability === key}
                  className={activeCapability === key ? "is-active" : ""}
                  onClick={() => setActiveCapability(key)}
                >
                  <span>{item.index}</span>
                  {item.label}
                  <i aria-hidden="true">→</i>
                </button>
              );
            })}
          </div>

          <article className="capability-detail" role="tabpanel">
            <div className="capability-copy">
              <span className="section-kicker">{capability.eyebrow}</span>
              <h3>{capability.title}</h3>
              <p>{capability.copy}</p>
              <ul>
                {capability.bullets.map((bullet) => (
                  <li key={bullet}><span>✓</span>{bullet}</li>
                ))}
              </ul>
            </div>
            <div className={`capability-visual capability-visual--${activeCapability}`}>
              <div className="cap-orbit cap-orbit--one" />
              <div className="cap-orbit cap-orbit--two" />
              <div className="cap-code">{capability.index}</div>
              <div className="cap-stats">
                {capability.stats.map((stat) => (
                  <div key={stat.label}><strong>{stat.value}</strong><span>{stat.label}</span></div>
                ))}
              </div>
            </div>
          </article>
        </div>
      </section>

      <section className="architecture section-pad" id="architecture">
        <div className="section-shell">
          <div className="section-intro section-intro--light">
            <div>
              <span className="section-kicker">End-to-end command governance</span>
              <h2>From human intent to verified state.</h2>
            </div>
            <p>
              HUD preserves one traceable route for wall-panel, operator and automation
              actions. Select a stage to see why the command lifecycle matters.
            </p>
          </div>

          <div className="trace-layout">
            <div className="trace-rail" role="tablist" aria-label="Command lifecycle">
              {commandSteps.map((step, index) => (
                <button
                  key={step.title}
                  type="button"
                  className={traceStep === index ? "is-active" : traceStep > index ? "is-complete" : ""}
                  onClick={() => setTraceStep(index)}
                >
                  <i>{traceStep > index ? "✓" : index + 1}</i>
                  <span>{step.title}</span>
                </button>
              ))}
            </div>
            <div className="trace-stage">
              <div className="trace-stage-head">
                <span>Stage {traceStep + 1} of {commandSteps.length}</span>
                <span className="trace-live"><i /> Evidence path</span>
              </div>
              <strong>{commandSteps[traceStep].title}</strong>
              <p>{commandSteps[traceStep].body}</p>
              <div className="trace-packet">
                <span>DeviceCommand</span>
                <code>{`hud_cmd_${String(traceStep + 1).padStart(2, "0")}`}</code>
                <i>→</i>
                <span>{traceStep === 5 ? "state: reconciled" : `stage: ${commandSteps[traceStep].title.toLowerCase()}`}</span>
              </div>
              <button
                className="button button--cyan"
                type="button"
                onClick={() => setTraceStep((traceStep + 1) % commandSteps.length)}
              >
                Next stage <span>→</span>
              </button>
            </div>
          </div>

          <div className="architecture-layers">
            <article>
              <span>01</span><h3>SaaS control plane</h3>
              <p>Portfolio context, identity, policy, commands, automation and fleet intelligence.</p>
            </article>
            <article>
              <span>02</span><h3>Edge runtime</h3>
              <p>Gateway ownership, local registry, discovery, execution, persistence and recovery.</p>
            </article>
            <article>
              <span>03</span><h3>Protocol adapters</h3>
              <p>Home Assistant and HomeKit paths today, with a registry built to expand deliberately.</p>
            </article>
            <article>
              <span>04</span><h3>Physical state</h3>
              <p>Device result reporting and reconciliation return reality to the operator.</p>
            </article>
          </div>
        </div>
      </section>

      <section className="wall-panel section-shell section-pad" id="wall-panel">
        <div className="wall-copy">
          <span className="section-kicker">Resident and operator experience</span>
          <h2>The building becomes touchable.</h2>
          <p>
            Published rooms and runtime-bound devices become a responsive HUD surface
            for mounted panels, tablets and phones—supported by readiness, binding,
            queue, audit and operator diagnostics.
          </p>
          <div className="wall-points">
            <div><strong>Graphical rooms</strong><span>Navigate the physical space, not a device spreadsheet.</span></div>
            <div><strong>Local continuity</strong><span>Bounded controls can route through the Edge runtime when configured.</span></div>
            <div><strong>Field evidence</strong><span>Readiness, audit export and reconciliation support accountable operation.</span></div>
          </div>
          <div className="truth-note">
            <span>Current baseline</span>
            Browser/kiosk delivery is implemented. An installable offline PWA lifecycle is not yet included.
          </div>
        </div>

        <div className="panel-demo" aria-label="Interactive wall panel demonstration">
          <div className="panel-frame">
            <div className="panel-topbar">
              <div><Mark small /><strong>HUD</strong><span>HOME MODE</span></div>
              <div><i /> EDGE + CLOUD <time>8:42 PM</time></div>
            </div>
            <div className="panel-main">
              <div className="panel-room-title">
                <span>MAIN FLOOR / OFFICE</span>
                <strong>Office</strong>
              </div>
              <div className="device-bubble">
                <button
                  type="button"
                  className={demoLight ? "light-orb is-on" : "light-orb"}
                  onClick={() => setDemoLight((value) => !value)}
                  aria-label={demoLight ? "Turn demonstration light off" : "Turn demonstration light on"}
                >
                  <span>☀</span>
                </button>
                <div>
                  <strong>Office Dimmer</strong>
                  <span>{demoLight ? `ON · ${brightness}%` : "OFF"}</span>
                </div>
              </div>
              <label className="brightness-control">
                <span>Brightness <output>{brightness}%</output></span>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={brightness}
                  onChange={(event) => {
                    setBrightness(Number(event.target.value));
                    setDemoLight(Number(event.target.value) > 0);
                  }}
                />
              </label>
              <div className="zone-row">
                <button type="button"><i />Zone 01<span>Ready</span></button>
                <button type="button"><i />Zone 02<span>Ready</span></button>
                <button type="button"><i />Zone 03<span>Ready</span></button>
              </div>
              <div className="panel-foot">
                <span><i /> Runtime ready</span>
                <span>Interactive product model · no device command sent</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="operations section-pad" id="operations">
        <div className="section-shell">
          <div className="section-intro">
            <div>
              <span className="section-kicker">Enterprise operations</span>
              <h2>See the portfolio before it becomes a problem.</h2>
            </div>
            <p>
              HUD rolls live runtime data into role-aligned views for people who manage
              buildings, gateways, devices, commands and automation reliability.
            </p>
          </div>

          <div className="operations-grid">
            <article className="ops-primary">
              <div className="ops-head">
                <div><span>Portfolio runtime</span><strong>Operational picture</strong></div>
                <button type="button">Last 24 hours⌄</button>
              </div>
              <div className="ops-score">
                <div className="score-ring"><strong>94</strong><span>readiness</span></div>
                <div className="score-bars">
                  {[
                    ["Gateway freshness", 96],
                    ["Device readiness", 91],
                    ["Command completion", 97],
                    ["Automation reliability", 92],
                  ].map(([label, value]) => (
                    <div key={String(label)}>
                      <span>{label}<b>{value}%</b></span>
                      <i><em style={{ width: `${value}%` }} /></i>
                    </div>
                  ))}
                </div>
              </div>
              <small>Illustrative interface model; values are not live production data.</small>
            </article>

            <div className="ops-cards">
              {[
                ["Manager Dashboard", "Runtime summary, readiness, integrity and attention items."],
                ["Building Comparison", "Health and readiness across buildings with drilldown."],
                ["Gateway Fleet Health", "Activity, freshness, device count, usage and rankings."],
                ["Device Fleet Health", "Connectivity, readiness, command and state risk."],
                ["Automation Fleet", "Execution reliability, completion, latency and risk."],
                ["AI Operations", "Read-only observations, guardrails, diagnostics and evidence."],
              ].map(([title, copy], index) => (
                <article key={title}>
                  <span>{String(index + 1).padStart(2, "0")}</span>
                  <h3>{title}</h3>
                  <p>{copy}</p>
                </article>
              ))}
            </div>
          </div>

          <div className="roles">
            <div className="roles-heading">
              <span className="section-kicker">One platform, four perspectives</span>
              <h3>Give each person the right operating altitude.</h3>
            </div>
            <div className="role-grid">
              {roleCards.map((role, index) => (
                <article key={role.role}>
                  <span className="role-number">0{index + 1}</span>
                  <strong>{role.role}</strong>
                  <small>{role.tag}</small>
                  <p>{role.copy}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="readiness section-shell section-pad" id="readiness">
        <div className="section-intro">
          <div>
            <span className="section-kicker">Evidence over exaggeration</span>
            <h2>Built to grow through controlled proof.</h2>
          </div>
          <p>
            HUD distinguishes implemented capability, trial-hardening work and future
            integrations. That clarity is part of the product—not a footnote.
          </p>
        </div>

        <div className="readiness-grid">
          <article className="readiness-card readiness-card--now">
            <div className="readiness-title"><span>✓</span><div><small>Source-verified</small><h3>Implemented baseline</h3></div></div>
            <ul>
              <li>Portfolio hierarchy, memberships and tenant assignments</li>
              <li>Gateway enrollment, scoped ownership and heartbeat</li>
              <li>Home Assistant discovery/import and HomeKit pairing path</li>
              <li>DeviceCommand lifecycle, state reporting and reconciliation</li>
              <li>Automation definitions, schedules and execution history</li>
              <li>Fleet dashboards, read-only AI and browser wall panel</li>
            </ul>
          </article>
          <article className="readiness-card readiness-card--work">
            <div className="readiness-title"><span>↻</span><div><small>Certification program</small><h3>Trial hardening</h3></div></div>
            <ul>
              <li>30-apartment / four-floor isolation and load evidence</li>
              <li>Complete role enforcement across structural and device paths</li>
              <li>Gateway credential rotation, revocation and device lifecycle</li>
              <li>Restart, concurrency, recovery and offline reconciliation proof</li>
              <li>Physical dimmer, lighting and sprinkler regression packs</li>
              <li>Installable PWA and managed wall-panel lifecycle</li>
            </ul>
          </article>
          <article className="readiness-card readiness-card--future">
            <div className="readiness-title"><span>＋</span><div><small>Deliberate expansion</small><h3>Provider roadmap</h3></div></div>
            <p>
              The runtime registry is designed to accept additional protocol adapters.
              Matter source is present but awaits physical certification; other provider
              names shown in the current UI remain placeholders until their backend and
              field evidence are complete.
            </p>
            <div className="provider-tags">
              <span>Home Assistant <b>active</b></span>
              <span>HomeKit / HAP <b>advanced</b></span>
              <span>Matter <b>certifying</b></span>
              <span>Additional adapters <b>roadmap</b></span>
            </div>
          </article>
        </div>
      </section>

      <section className="business section-pad">
        <div className="section-shell">
          <span className="section-kicker">Where HUD fits</span>
          <div className="business-heading">
            <h2>From one residence to a connected property portfolio.</h2>
            <p>One foundation, adapted to the operating model—not rebuilt for every site.</p>
          </div>
          <div className="business-grid">
            <article>
              <span className="business-index">01</span>
              <div className="business-graphic business-graphic--multi"><i /><i /><i /></div>
              <h3>Multifamily residential</h3>
              <p>Apartment-scoped experiences, common-area operations, floor layouts and a portfolio control plane.</p>
              <strong>Current focus: 30-apartment trial program</strong>
            </article>
            <article>
              <span className="business-index">02</span>
              <div className="business-graphic business-graphic--home"><i /><i /></div>
              <h3>Private residences</h3>
              <p>Graphical room control, local continuity, Home Assistant and HomeKit integration in one governed experience.</p>
              <strong>Designed for precise local operation</strong>
            </article>
            <article>
              <span className="business-index">03</span>
              <div className="business-graphic business-graphic--campus"><i /><i /><i /><i /></div>
              <h3>Hospitality and campuses</h3>
              <p>Repeatable buildings, scoped gateways, fleet health and evidence-led operations across a larger property.</p>
              <strong>Architecture ready for multi-site growth</strong>
            </article>
          </div>
        </div>
      </section>

      <section className="about section-shell section-pad" id="about">
        <div className="about-mark"><Mark /><span>JF</span></div>
        <div className="about-copy">
          <span className="section-kicker">Designed and developed by Julio Fernandez</span>
          <h2>Systems engineering applied to the physical world.</h2>
          <p>
            Julio Fernandez is a systems engineer, application developer and IT
            architect with decades of experience in secure infrastructure, connected
            environments, full-stack software and operational automation. HUD brings
            that experience into a disciplined platform for intelligent buildings.
          </p>
          <div className="about-skills">
            <span>Enterprise architecture</span>
            <span>Full-stack development</span>
            <span>IoT integration</span>
            <span>Runtime operations</span>
          </div>
        </div>
        <div className="about-meta">
          <span>Based in</span><strong>Biscayne Park, Florida</strong>
          <span>Platform owner</span><strong>HUD Holdings LLC</strong>
          <span>Contact</span><a href="mailto:julio@jpsecurity.net">Julio@JPSecurity.Net</a>
        </div>
      </section>

      <section className="contact section-shell" id="contact">
        <div className="contact-glow" />
        <div>
          <span className="section-kicker">Private platform briefing</span>
          <h2>Let’s map HUD to your property.</h2>
          <p>
            Discuss your buildings, existing automation systems, operating needs and
            trial objectives directly with the platform designer.
          </p>
        </div>
        <a
          className="button button--white"
          href="mailto:julio@jpsecurity.net?subject=HUD%20Platform%20Briefing"
        >
          Request a briefing <Arrow />
        </a>
      </section>

      <footer>
        <div className="footer-brand"><Mark small /><span><strong>HUD</strong> Smart Building Platform</span></div>
        <p>© 2026 HUD Holdings LLC. Design and development by Julio Fernandez.</p>
        <div><a href="#platform">Platform</a><a href="#readiness">Readiness</a><a href="mailto:julio@jpsecurity.net">Contact</a></div>
      </footer>
    </main>
  );
}
